import React, { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Col, Row } from 'reactstrap';
import { Form, FormControl } from 'react-bootstrap';
import classnames from 'classnames';
import Button from '../../../../consulta-acessibilidade/Button';
import { useTranslation } from 'react-i18next';
import { useAccessibilityContext } from '../../../../consulta-acessibilidade/contexts/AccessibilityContext';
import * as ThesaurusService from '../../../../../shared/services/ThesaurusService';
import Axios, { CancelTokenSource } from 'axios';
import { APIHelper } from '../../../../../shared/helpers';
import { CommonListItemType } from '../../../../../shared/types/general';
import { useDispatch } from 'react-redux';
import { useAppContext } from '../../../../../shared/contexts/AppContextProvider';
import { useThesaurusContext } from '../../../../../shared/contexts/ThesaurusSearchContextProvider';
interface ThesaurusSearchProps {
    error: any;
    loading: boolean;
}

const schema = z
    .object({
        searchTerm: z.string().min(1, 'min1'),
        searchOption: z.string(),
    })
    .superRefine((data, ctx) => {});

type FormData = z.infer<typeof schema>;

const ThesaurusSearch: React.FC<ThesaurusSearchProps> = ({
    error,
    loading,
}) => {
    const { t } = useTranslation();
    const { searchInputRef } = useAccessibilityContext();
    const [searchOptions, setSearchOptions] = useState<
        CommonListItemType[] | undefined
    >(undefined);
    const {
        changeSearchOption,
        changeSearchTherm,
        changeSearchType,
        searchType,
        search,
        searchOption: currentSearchOption,
    } = useThesaurusContext();

    const schema = useMemo(() => {
        return z
            .object({
                searchTerm: z.string(),
                searchOption: z.string(),
            })
            .superRefine((data, ctx) => {
                if (searchType === 'term' && data.searchTerm.trim() === '') {
                    ctx.addIssue({
                        path: ['searchTerm'],
                        message: 'Campo obrigatório',
                        code: z.ZodIssueCode.custom,
                    });
                }
            });
    }, [searchType]);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
    } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            searchTerm: '',
            searchOption: '-1',
        },
    });

    const searchOption = watch('searchOption');
    const searchTherm = watch('searchTerm');

    useEffect(() => {
        changeSearchOption(searchOption);
    }, [searchOption]);

    useEffect(() => {
        changeSearchTherm(searchTherm);
    }, [searchTherm]);

    useEffect(() => {
        reset({ searchTerm: '', searchOption: '-1' });
        if (searchType === 'tree' && searchOptions?.length) {
            setValue('searchOption', `${searchOptions[0].cod}`);
        }
    }, [searchType]);

    useEffect(() => {
        const cancelTokenSrc: CancelTokenSource = Axios.CancelToken.source();
        const { token } = cancelTokenSrc;

        ThesaurusService.getSearchOptions(token)
            .then(response => {
                const { data } = response;
                if (data) {
                    setSearchOptions(data);
                }
            })
            .catch(error => {
                if (!Axios.isCancel(error)) {
                    APIHelper.tratarErro(error);
                }
            });
    }, []);

    return (
        <Form onSubmit={handleSubmit(search)} className="p-3">
            <Row>
                <Col md="3" sm="12" className="input-holder mt-3">
                    <div className="rotulo">{t('TipoPesquisa')}</div>
                    <FormControl
                        as="select"
                        className="b-tema20 cortxt rad10 s14"
                        value={searchType}
                        onChange={(e: any) => {
                            changeSearchType(e.target.value as 'tree' | 'term');
                        }}
                    >
                        <option value="tree">{t('Arvore')}</option>
                        <option value="term">{t('Termo')}</option>
                    </FormControl>
                </Col>
                {searchType === 'term' ? (
                    <Col md="5" sm="12" className="input-holder mt-3">
                        <div className="rotulo">{t('CampoBusca')}</div>
                        <FormControl
                            {...register('searchTerm')}
                            ref={(e: any) => {
                                register('searchTerm').ref(e);
                                searchInputRef.current = e;
                            }}
                            type="search"
                            className="rad10 b-tema20 cortxt foco"
                            placeholder={t('DigiteSuaBuscaAqui')}
                            isInvalid={errors.searchTerm !== undefined}
                        />
                        {errors.searchTerm ? (
                            <FormControl.Feedback type="invalid">
                                {t(errors.searchTerm?.message as any)}
                            </FormControl.Feedback>
                        ) : (
                            ''
                        )}
                    </Col>
                ) : (
                    ''
                )}
                {searchOptions ? (
                    <Col md="2" sm="12" className="input-holder mt-3">
                        <div className="rotulo">{t('TipoThesauro')}</div>
                        <FormControl
                            as="select"
                            className="b-tema20 cortxt rad10 s14"
                            {...register('searchOption')}
                        >
                            { searchType === 'term' &&  <option value="-1">{t('Todos')}</option>  }
                            {searchOptions.map(term => (
                                <option
                                    key={`termo-${term.cod}`}
                                    value={term.cod}
                                >
                                    {term.description}
                                </option>
                            ))}
                        </FormControl>
                    </Col>
                ) : (
                    ''
                )}
                <Col
                    md="2"
                    sm="12"
                    className={classnames('mt-3 align-self-end pb-0', {
                        'mb-4': error,
                    })}
                >
                    <Button
                        title="Pesquisar"
                        icon="search"
                        variant="primeiro ml-0 btn-responsivo"
                        type="submit"
                        disabled={loading}
                    />
                </Col>
            </Row>
        </Form>
    );
};

export default ThesaurusSearch;
